'use client';

import { useState } from 'react';

import { cn } from '@/lib/utils';
import type { ServiceAudienceSectionData } from '@/sanity/types';

interface ServiceAudienceProps {
  data: ServiceAudienceSectionData;
}

/**
 * ServiceAudience — "Who this service is for". Centered H2 (accent +
 * dark), a pill tab switcher, and a
 * three-card grid whose content swaps per tab. WAI-ARIA tabs pattern with
 * arrow-key navigation; cards are re-announced via aria-live on the panel.
 */
export function ServiceAudience({ data }: ServiceAudienceProps) {
  const tabs = data.audienceTabs ?? [];
  const [activeIndex, setActiveIndex] = useState(0);

  if (tabs.length === 0) return null;

  const activeTab = tabs[activeIndex] ?? tabs[0];
  const cards = activeTab?.cards ?? [];

  function handleKey(
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    const last = tabs.length - 1;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex(index === last ? 0 : index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex(index === 0 ? last : index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(last);
    }
  }

  return (
    <section
      aria-labelledby="service-audience-heading"
      data-section-id="service_audience"
      className="bg-foreground py-16 md:py-20 lg:py-24 xl:py-28"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <h2
          id="service-audience-heading"
          className="text-center font-light leading-[0.95] text-[36px] md:text-[56px] lg:text-[72px] xl:text-[88px] tracking-[-0.019em] md:tracking-[-0.026em] lg:tracking-[-0.029em] xl:tracking-[-0.031em]"
        >
          <span className="text-accent-ink">{data.headingAccent}</span>{' '}
          <span className="text-[#121212]">{data.headingText}</span>
        </h2>

        {/* ── Tab switcher ── */}
        <div
          role="tablist"
          aria-label="Audience segments"
          className="mt-8 flex flex-wrap justify-center gap-2.5 lg:mt-12"
        >
          {tabs.map((tab, index) => {
            const selected = index === activeIndex;
            return (
              <button
                key={`${tab.label}-${index}`}
                id={`audience-tab-${index}`}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls="audience-tabpanel"
                tabIndex={selected ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(event) => handleKey(event, index)}
                data-analytics-cta={`audience_tab_${index + 1}`}
                data-analytics-location="service_audience"
                className={cn(
                  'flex cursor-pointer items-center gap-1.5 rounded-[10px] border px-5 py-2.5 text-[13px] font-normal uppercase tracking-[-0.02em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 lg:text-[15px]',
                  selected
                    ? 'border-accent bg-accent text-[#121212]'
                    : 'border-[#cbcbcb] bg-transparent text-[#3d3d3d] hover:text-[#121212]',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Cards for the active tab ── */}
        <div
          id="audience-tabpanel"
          role="tabpanel"
          aria-labelledby={`audience-tab-${activeIndex}`}
          aria-live="polite"
          tabIndex={0}
          className="mt-8 grid grid-cols-1 gap-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 md:grid-cols-3 lg:mt-12 lg:gap-6"
        >
          {cards.map((card, index) => {
            const cardItems = card.items ?? [];
            return (
              <div
                key={index}
                className="rounded-[12px] border border-[#cbcbcb] p-6 lg:p-8"
              >
                {card.title && (
                  <h3 className="text-[20px] font-light leading-none tracking-[-0.02em] text-[#121212] md:text-[24px] lg:text-[26px]">
                    {card.title}
                  </h3>
                )}
                <ul
                  role="list"
                  className={cn(
                    'mt-6',
                    cardItems.length > 4
                      ? 'grid grid-cols-2 gap-x-6 gap-y-3'
                      : 'flex flex-col gap-3',
                  )}
                >
                  {cardItems.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex gap-2.5 text-[15px] font-light leading-[1.36] text-[#3d3d3d] lg:text-[16px]"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-[0.45em] inline-block size-[7px] shrink-0 rounded-full bg-accent"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
